import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, Minus, Plus, Truck, Shirt, Sparkles, ChevronRight, ChevronLeft, Star, MessageSquare, ZoomIn, X, Info } from "lucide-react";
import { api, inr, setSeo, setJsonLd, SITE_URL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SITE } from "@/data/site";
import ProductGrid from "@/components/ProductGrid";
import { ProductReviews } from "@/components/ProductReviews";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, inWishlist } = useCart();

  const { data: product, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: async () => (await api.get(`/products/${slug}`)).data });
  const { data: related = [] } = useQuery({ queryKey: ["related", slug], queryFn: async () => (await api.get(`/products/${slug}/related`)).data, enabled: !!product });

  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  const hasVariants = !!product?.variants?.length;
  const colors = hasVariants ? product.variants.map((v) => v.color).filter(Boolean) : (product?.colors || []);
  const activeVariant = hasVariants ? (product.variants.find((v) => v.color === color) || product.variants[0]) : null;
  let allImages = hasVariants
    ? product.variants.flatMap((v) => (v.images || []).map((img) => ({ img, color: v.color })))
    : (product?.images || []).map((img) => ({ img, color: null }));
  if (!allImages.length) allImages = (product?.images || []).map((img) => ({ img, color: null }));
  const gallery = allImages.map((x) => x.img);
  const rawSizes = activeVariant ? (activeVariant.sizes || []) : (product?.sizes || []);
  const sizeList = rawSizes.map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
  const firstInStock = (arr) => (arr.find((s) => (s.stock ?? 0) > 0)?.size ?? arr[0]?.size);

  useEffect(() => {
    if (product) {
      const initColor = hasVariants ? product.variants[0]?.color : product.colors?.[0];
      const v0sizes = (hasVariants ? (product.variants[0]?.sizes || []) : (product.sizes || [])).map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
      setColor(initColor);
      setSize(firstInStock(v0sizes));
      setActiveImg(0);
      setQty(1);
      const img = (hasVariants ? product.variants[0]?.images?.[0] : null) || product.images?.[0];
      setSeo(`${product.name} | Adam's The Islamic Boutique`, `${product.description?.slice(0, 155)}`, img);
      setJsonLd("product-jsonld", {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        image: product.images || [],
        description: product.description || "",
        sku: product.id,
        category: product.category,
        brand: { "@type": "Brand", name: "Adam's The Islamic Boutique" },
        offers: {
          "@type": "Offer",
          url: `${SITE_URL}/product/${product.slug}`,
          priceCurrency: "INR",
          price: product.discount_price || product.price,
          availability: (product.stock ?? 1) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
        ...(product.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: 24 } } : {}),
      });
    }
    return () => setJsonLd("product-jsonld", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const sizesForColor = (c) => {
    const v = product.variants?.find((x) => x.color === c);
    return (v?.sizes || []).map((s) => (typeof s === "string" ? { size: s, stock: 1 } : { size: s.size, stock: s.stock ?? 0 }));
  };
  // Keep sizes/color in sync with whichever image is on screen
  const syncColorFromImage = (i) => {
    if (!hasVariants) return;
    const c = allImages[i]?.color;
    if (c && c !== color) {
      setColor(c);
      setSize(firstInStock(sizesForColor(c)));
    }
  };
  const selectImg = (i) => { setActiveImg(i); syncColorFromImage(i); };
  // Picking a color scrolls the gallery to that color's first photo
  const onSelectColor = (c) => {
    setColor(c);
    if (hasVariants) {
      setSize(firstInStock(sizesForColor(c)));
      const idx = allImages.findIndex((x) => x.color === c);
      setActiveImg(idx >= 0 ? idx : 0);
    }
  };
  const goPrevImg = () => selectImg((activeImg - 1 + gallery.length) % gallery.length);
  const goNextImg = () => selectImg((activeImg + 1) % gallery.length);

  if (isLoading || !product) {
    return <div className="max-w-[1400px] mx-auto px-6 py-20"><div className="animate-pulse grid lg:grid-cols-2 gap-10"><div className="aspect-[3/4] bg-beige rounded-2xl" /><div className="space-y-4"><div className="h-8 bg-beige rounded w-2/3" /><div className="h-4 bg-beige rounded w-1/3" /></div></div></div>;
  }

  const price = product.discount_price || product.price;
  const hasDiscount = !!product.discount_price;
  const wished = inWishlist(product.id);

  const buyNow = () => { addToCart(product, { color, size, quantity: qty, image: gallery[activeImg] }, true); navigate("/checkout"); };
  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(`Hi Adam's! I'm interested in "${product.name}" (${inr(price)}). Could you share more details?`)}`;

  const selectedSizeObj = sizeList.find((s) => s.size === size);
  const soldOut = sizeList.length > 0 && (!selectedSizeObj || (selectedSizeObj.stock ?? 0) <= 0);
  const lowStock = selectedSizeObj && selectedSizeObj.stock > 0 && selectedSizeObj.stock <= 5;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
      {/* breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-espresso/50 mb-8">
        <Link to="/" className="hover:text-espresso">Home</Link><ChevronRight className="w-3 h-3" />
        <Link to={`/category/${product.category}`} className="hover:text-espresso capitalize">{product.category.replace("-", " ")}</Link><ChevronRight className="w-3 h-3" />
        <span className="text-espresso/80">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse lg:flex-row lg:items-start gap-4 w-full max-w-[340px] sm:max-w-[400px] lg:max-w-[500px] mx-auto">
          <div className="flex lg:flex-col gap-3 no-scrollbar overflow-x-auto shrink-0">
            {allImages.map(({ img, color: ic }, i) => (
              <button key={`${img}-${i}`} onClick={() => selectImg(i)} className={`w-14 h-[72px] lg:w-16 lg:h-20 shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${activeImg === i ? "border-champagne" : ic && ic === color ? "border-champagne/40" : "border-transparent"}`} data-testid={`thumb-${i}`} title={ic || product.name}>
                <img src={img} alt={`${product.name} ${ic || ""} ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="relative w-full lg:flex-1 rounded-3xl overflow-hidden bg-beige aspect-[3/4] group">
            <img src={gallery[activeImg]} alt={product.name} onClick={() => { setLightbox(true); setZoomed(false); }} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 cursor-zoom-in" data-testid="product-main-image" />
            <button onClick={() => { setLightbox(true); setZoomed(false); }} aria-label="Zoom image" data-testid="pdp-zoom-btn" className="absolute top-3 right-3 w-10 h-10 rounded-full bg-ivory/85 backdrop-blur flex items-center justify-center shadow hover:bg-ivory transition-colors z-10">
              <ZoomIn className="w-5 h-5 text-espresso" />
            </button>
            {gallery.length > 1 && (
              <>
                <button onClick={goPrevImg} aria-label="Previous image" data-testid="pdp-img-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/85 backdrop-blur flex items-center justify-center shadow hover:bg-ivory transition-colors">
                  <ChevronLeft className="w-5 h-5 text-espresso" />
                </button>
                <button onClick={goNextImg} aria-label="Next image" data-testid="pdp-img-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/85 backdrop-blur flex items-center justify-center shadow hover:bg-ivory transition-colors">
                  <ChevronRight className="w-5 h-5 text-espresso" />
                </button>
                <div className="absolute bottom-4 inset-x-0 flex justify-center gap-1.5">
                  {gallery.map((_, d) => (
                    <button key={`gd-${d}`} onClick={() => selectImg(d)} aria-label={`Go to image ${d + 1}`} className={`h-1.5 rounded-full transition-all ${d === activeImg ? "w-5 bg-espresso" : "w-1.5 bg-espresso/40"}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="overline mb-2 capitalize">{product.category.replace("-", " ")}</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-espresso leading-tight" data-testid="product-title">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={`star-${i}`} className="w-4 h-4 fill-champagne text-champagne" />)}</div>
            <span className="text-xs text-espresso/50">{product.rating} · Loved by many</span>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <span className="font-sans text-2xl font-semibold text-espresso" data-testid="product-price">{inr(price)}</span>
            {hasDiscount && <><span className="line-through text-espresso/40">{inr(product.price)}</span><span className="bg-champagne/20 text-[#8a6d1e] text-xs font-semibold px-2.5 py-1 rounded-full">Save {inr(product.price - product.discount_price)}</span></>}
          </div>

          {/* Colors */}
          {colors.length > 0 && (
            <div className="mt-7">
              <p className="text-xs uppercase tracking-wider text-espresso/60 mb-2.5">Color: <span className="text-espresso font-medium">{color}</span></p>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => (
                  <button key={c} onClick={() => onSelectColor(c)} className={`px-4 py-2 rounded-full text-sm border ${color === c ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60 hover:border-espresso/40"}`} data-testid={`color-${c}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {sizeList.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-espresso/60 mb-2.5">Size: <span className="text-espresso font-medium">{size}</span></p>
              <div className="flex gap-2 flex-wrap">
                {sizeList.map((s) => {
                  const out = (s.stock ?? 0) <= 0;
                  return (
                    <button key={s.size} onClick={() => !out && setSize(s.size)} disabled={out} className={`min-w-[48px] px-4 py-2 rounded-full text-sm border relative ${out ? "border-espresso/10 text-espresso/30 line-through cursor-not-allowed" : size === s.size ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60 hover:border-espresso/40"}`} data-testid={`size-${s.size}`} title={out ? "Out of stock" : `${s.stock} in stock`}>{s.size}</button>
                  );
                })}
              </div>
              {soldOut ? (
                <p className="text-xs text-[#a13b2f] mt-2" data-testid="size-soldout">This size is currently sold out. Please choose another size or color.</p>
              ) : lowStock ? (
                <p className="text-xs text-[#8a6d1e] mt-2" data-testid="size-lowstock">Only {selectedSizeObj.stock} left in {size}!</p>
              ) : null}
            </div>
          )}

          {/* Quantity + actions */}
          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-espresso/20 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-11 h-11 flex items-center justify-center" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => toggleWishlist(product)} className="w-11 h-11 rounded-full border border-espresso/20 flex items-center justify-center hover:border-champagne" data-testid="pdp-wishlist"><Heart className={`w-5 h-5 ${wished ? "fill-[#a13b2f] text-[#a13b2f]" : "text-espresso"}`} /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            <button onClick={() => addToCart(product, { color, size, quantity: qty, image: gallery[activeImg] })} disabled={soldOut} className="bg-espresso text-ivory py-4 rounded-full text-sm font-medium hover:bg-espresso/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="pdp-add-cart">{soldOut ? "Sold Out" : "Add to Bag"}</button>
            <button onClick={buyNow} disabled={soldOut} className="bg-champagne text-espresso py-4 rounded-full text-sm font-semibold hover:bg-champagne-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="pdp-buy-now">Buy Now</button>
          </div>

          <a href={waLink} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 mt-3 border border-[#25D366]/40 text-[#1d9e4d] py-3 rounded-full text-sm hover:bg-[#25D366]/5 transition-colors" data-testid="pdp-whatsapp">
            <svg viewBox="0 0 32 32" className="w-4 h-4 fill-[#1d9e4d]"><path d="M16 3C9.4 3 4 8.4 4 15c0 2.1.6 4.1 1.6 5.9L4 29l8.3-1.6c1.7.9 3.7 1.4 5.7 1.4 6.6 0 12-5.4 12-12S22.6 3 16 3z"/></svg>
            Enquire on WhatsApp
          </a>

          <p className="flex items-start gap-2 mt-5 text-xs text-espresso/50 italic" data-testid="pdp-color-disclaimer">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 not-italic" />
            The colour and design may slightly vary due to photography and editing.
          </p>

          {/* Details accordion */}
          <Accordion type="single" collapsible className="mt-8 border-t border-champagne/20" defaultValue="details">
            <AccordionItem value="details" className="border-b border-champagne/20">
              <AccordionTrigger className="text-sm font-medium text-espresso hover:no-underline"><span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-champagne" /> Product Details</span></AccordionTrigger>
              <AccordionContent className="text-sm text-espresso/65 leading-relaxed">
                {(product.description || "").split(/\n+/).map((s) => s.trim()).filter(Boolean).map((para, i) => (
                  <p key={`desc-${i}`} className="mb-3 last:mb-0">{para}</p>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="fabric" className="border-b border-champagne/20">
              <AccordionTrigger className="text-sm font-medium text-espresso hover:no-underline"><span className="flex items-center gap-2"><Shirt className="w-4 h-4 text-champagne" /> Fabric & Care</span></AccordionTrigger>
              <AccordionContent className="text-sm text-espresso/65 leading-relaxed"><strong className="text-espresso">Fabric:</strong> {product.fabric}<br /><strong className="text-espresso">Care:</strong> {product.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="delivery" className="border-b border-champagne/20">
              <AccordionTrigger className="text-sm font-medium text-espresso hover:no-underline"><span className="flex items-center gap-2"><Truck className="w-4 h-4 text-champagne" /> Delivery & Returns</span></AccordionTrigger>
              <AccordionContent className="text-sm text-espresso/65 leading-relaxed">{product.delivery}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="reviews" className="border-b border-champagne/20">
              <AccordionTrigger className="text-sm font-medium text-espresso hover:no-underline" data-testid="reviews-accordion-trigger"><span className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-champagne" /> Customer Reviews</span></AccordionTrigger>
              <AccordionContent><ProductReviews slug={product.slug} productId={product.id} /></AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-20 lg:mt-28">
          <h2 className="font-serif text-3xl lg:text-4xl text-espresso mb-10 text-center">You May Also Like</h2>
          <ProductGrid products={related} cols={4} />
        </div>
      )}

      {/* Zoom lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-espresso/95 flex items-center justify-center fade-in" data-testid="pdp-lightbox" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} aria-label="Close" data-testid="pdp-lightbox-close" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-ivory/15 hover:bg-ivory/25 text-ivory flex items-center justify-center transition-colors z-10">
            <X className="w-6 h-6" />
          </button>
          {gallery.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goPrevImg(); setZoomed(false); }} aria-label="Previous image" data-testid="pdp-lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/15 hover:bg-ivory/25 text-ivory flex items-center justify-center transition-colors z-10">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); goNextImg(); setZoomed(false); }} aria-label="Next image" data-testid="pdp-lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/15 hover:bg-ivory/25 text-ivory flex items-center justify-center transition-colors z-10">
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="w-[92vw] h-[88vh] flex items-center justify-center overflow-hidden">
            <img
              src={gallery[activeImg]}
              alt={product.name}
              data-testid="pdp-lightbox-image"
              onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
              onMouseMove={(e) => {
                if (!zoomed) return;
                const r = e.currentTarget.getBoundingClientRect();
                setOrigin({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
              style={{ transform: zoomed ? "scale(2.2)" : "scale(1)", transformOrigin: `${origin.x}% ${origin.y}%` }}
              className={`max-w-full max-h-full object-contain rounded-lg transition-transform duration-200 ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            />
          </div>
          <p className="absolute bottom-5 inset-x-0 text-center text-ivory/70 text-xs tracking-wide">{zoomed ? "Click image to zoom out" : "Click image to zoom in"}</p>
        </div>
      )}
    </div>
  );
}
